import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useWallet } from '../../../chain/WalletContext';

const API = process.env.REACT_APP_APP_API_URL;
const authHeaders = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
const PLAN_ORDER = ['starter', 'pro', 'elite'];

export default function Upgrade() {
  const { address, connect, payBRC } = useWallet();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // plan key being purchased

  const load = useCallback(() => {
    axios.get(`${API}/api/subscription`, { headers: authHeaders() })
      .then(res => { if (res.data.status) setData(res.data); })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const buy = async (planKey) => {
    if (!data || !data.payTo) { toast.error('Subscriptions are not configured yet'); return; }
    const plan = data.plans[planKey];
    setBusy(planKey);
    try {
      let addr = address;
      if (!addr) {
        addr = await connect();
        if (!addr) { setBusy(null); return; }
      }
      const txHash = await payBRC(data.payTo, plan.priceBRC);
      // give the chain a moment, retrying verification briefly
      let ok = false;
      for (let i = 0; i < 10; i++) {
        const res = await axios.post(`${API}/api/subscription/pay`, { plan: planKey, txHash }, { headers: authHeaders() });
        if (res.data.status) { ok = true; break; }
        if (!/not yet mined|not found/i.test(res.data.message || '')) throw new Error(res.data.message);
        await new Promise(r => setTimeout(r, 2500));
      }
      if (!ok) throw new Error('Payment sent but not verified yet — refresh in a minute');
      toast.success(`${plan.name} plan activated!`);
      load();
    } catch (err) {
      const msg = (err && err.message) || 'Could not complete the upgrade';
      toast.error(/user (rejected|denied)/i.test(msg) ? 'Transaction cancelled in wallet' : msg);
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <p className="text-muted p-4">Loading plans…</p>;

  const current = data.current.plan;

  return (
    <div className="bp-plans-page">
      <div className="bp-plans-head">
        <h2>Grow your shop</h2>
        <p className="text-muted">
          Pay in BRC from your wallet — verified on-chain, active in seconds.
          {data.current.paidPlan && data.current.paidPlan !== 'starter' && data.current.expiresAt && (
            <> Your <strong>{data.plans[data.current.paidPlan].name}</strong> plan
              {current === 'starter' ? ' expired ' : ' renews by '}
              <strong>{moment(data.current.expiresAt).format('MMM D, YYYY')}</strong>.</>
          )}
        </p>
      </div>

      <div className="bp-plans-grid">
        {PLAN_ORDER.map(key => {
          const plan = data.plans[key];
          const isCurrent = current === key;
          const isPaid = plan.priceBRC > 0;
          return (
            <div className={`bp-plan-card ${key === 'pro' ? 'featured' : ''} ${isCurrent ? 'current' : ''}`} key={key}>
              {key === 'pro' && <span className="bp-plan-flag">Most popular</span>}
              <h3>{plan.name}</h3>
              <div className="bp-plan-price">
                {isPaid ? (
                  <><strong>{plan.priceBRC}</strong><span> BRC</span><em>/ {data.durationDays} days</em></>
                ) : (
                  <><strong>Free</strong><em>forever</em></>
                )}
              </div>
              <div className="bp-plan-limit">
                {plan.productLimit === null ? 'Unlimited products' : `Up to ${plan.productLimit} products`}
              </div>
              <ul>
                {plan.features.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
              {isCurrent ? (
                <button className="bp-plan-btn current" disabled>Current plan ✓</button>
              ) : isPaid ? (
                <button className="bp-plan-btn" onClick={() => buy(key)} disabled={!!busy}>
                  {busy === key ? 'Confirm in wallet…' : `Upgrade — pay ${plan.priceBRC} BRC`}
                </button>
              ) : (
                <button className="bp-plan-btn ghost" disabled>Included</button>
              )}
            </div>
          );
        })}
      </div>

      <p className="bp-plans-note text-muted">
        Payments go to the BirrPlug treasury and are verified on BRDPoSChain before activation.
        Renewing an active plan extends it by {data.durationDays} days.
      </p>
    </div>
  );
}
